import type { UseMutationResult } from 'react-query';
import { useMutation, useQueryClient } from 'react-query';

import type { IApiDeleteOrganizationResponseBody } from '../OrganizationsApi.types';
import { useApi } from '~/lib/api/useApi';
import { organizationQueryKeyFactory } from './useOrganizationQuery';
import { organizationsQueryKeyFactory } from './useOrganizationsQuery';
import { organizationUsersQueryKeyFactory } from './useOrganizationUsersQuery';

export type IUseDeleteOrganizationMutationValues = {
  organizationId: string;
};

export const useDeleteOrganizationMutation = (): UseMutationResult<
  IApiDeleteOrganizationResponseBody,
  unknown,
  IUseDeleteOrganizationMutationValues
> => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: IUseDeleteOrganizationMutationValues) =>
      api.organizations.delete(values.organizationId),
    onSuccess: async (data) => {
      data.deletedResources.organizationIds
        .map((organizationId) => [
          { queryKey: organizationQueryKeyFactory({ organizationId }) },
          { queryKey: organizationUsersQueryKeyFactory({ organizationId }) },
        ])
        .flat()
        .forEach((filters) => queryClient.removeQueries(filters));

      await Promise.all(
        [{ queryKey: organizationsQueryKeyFactory() }].map((filters) =>
          queryClient.invalidateQueries(filters),
        ),
      );
    },
  });
};
